// ✅ MobileNavbar.js — Clean bottom nav for worker app (no Jobs Overview)
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Briefcase,
  Car,
  Menu,
  LogOut,
  Mail,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const MobileNavbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/worker-login");
  };

  return (
    <>
      {/* ✅ Fixed Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.06)] border-t border-gray-200 flex justify-around items-center py-2">
        <Link
          to="/dashboard"
          className={`flex flex-col items-center ${
            isActive("/dashboard") ? "text-blue-600" : "text-gray-500"
          }`}
        >
          <Home size={22} />
          <span className="text-[11px] mt-0.5">Home</span>
        </Link>

        {/* ✅ Jobs (hub) */}
        <Link
          to="/jobs"
          className={`flex flex-col items-center ${
            isActive("/jobs") ||
            isActive("/my-jobs") ||
            isActive("/applied-jobs")
              ? "text-blue-600"
              : "text-gray-500"
          }`}
        >
          <Briefcase size={22} />
          <span className="text-[11px] mt-0.5">Jobs</span>
        </Link>
        {/*
        <Link
          to="/ride/post"
          className={`flex flex-col items-center ${
            isActive("/ride") ? "text-blue-600" : "text-gray-500"
          }`}
        >
          <Car size={22} />
          <span className="text-[11px] mt-0.5">Rides</span>
        </Link>
         */}
        <button
          onClick={() => setMenuOpen(true)}
          className="flex flex-col items-center text-gray-500"
        >
          <Menu size={22} />
          <span className="text-[11px] mt-0.5">Menu</span>
        </button>
      </nav>

      {/* ✅ Slide-Up Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Background overlay */}
            <motion.div
              className="fixed inset-0 bg-black/40 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />

            {/* Slide-up menu */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl p-6 shadow-lg"
            >
              <h2 className="text-lg font-semibold mb-3 text-gray-800">
                Menu
              </h2>

              <div className="grid grid-cols-2 gap-3 text-gray-700 text-sm">
                <Link
                  to="/inbox"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  <Mail size={18} /> Inbox
                </Link>

                <Link
                  to="/opportunities"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Opportunities
                </Link>

                {/* ✅ Jobs Hub & sub-links */}
                <Link
                  to="/jobs"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  <Briefcase size={18} /> Jobs Hub
                </Link>
                <Link
                  to="/my-jobs"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  My Jobs
                </Link>
                <Link
                  to="/applied-jobs"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Applied Jobs
                </Link>

                <Link
                  to="/worker-profile"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Profile
                </Link>
                <Link
                  to="/tier"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Tier
                </Link>
                <Link
                  to="/documents"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Documents
                </Link>
                <Link
                  to="/payments"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Payments
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Settings
                </Link>
                <Link
                  to="/public-profile"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Public Profile
                </Link>
                <Link
                  to="/help"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  Help
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-gray-50"
                >
                  About
                </Link>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full mt-4 flex items-center justify-center gap-2 text-red-600 font-semibold py-3 rounded-xl border border-red-200 hover:bg-red-50"
              >
                <LogOut size={18} /> Logout
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNavbar;
